import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, Clock, Copy, ExternalLink, Save,
  Trash2, CheckCircle2, X, Plus, Mail, Phone,
} from "lucide-react";

/* /dashboard/appointments — Mike's built-in 1-on-1 booking page.
   Member configures: title, intro, slot length, days-advance, weekly hours.
   Public visitors book at /m/{handle}/book — no third party required. */

const WINE = "#0F172A";
const O    = "#F39A1F";
const G    = "#2E9F3A";
const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const CREAM = "#FFFFFF";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [handle, setHandle] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/booking/settings"),
      api.get("/booking/mine"),
      api.get("/mirror/me").catch(() => ({ data: { handle: "" } })),
    ])
      .then(([a, b, c]) => {
        setS(a.data);
        setBookings(b.data?.bookings || []);
        setHandle(c.data?.handle || "");
      })
      .catch(() => toast.error("Couldn't load appointments."))
      .finally(() => setLoading(false));
  }, []);

  const update = (k, v) => { setS((p) => ({ ...p, [k]: v })); setDirty(true); };
  const updateDay = (dayKey, arr) => {
    setS((p) => ({ ...p, weekly: { ...p.weekly, [dayKey]: arr } }));
    setDirty(true);
  };

  const addWindow = (dayKey) => {
    const existing = s.weekly?.[dayKey] || [];
    updateDay(dayKey, [...existing, { start: "09:00", end: "17:00" }]);
  };
  const removeWindow = (dayKey, idx) => {
    const existing = s.weekly?.[dayKey] || [];
    updateDay(dayKey, existing.filter((_, i) => i !== idx));
  };
  const editWindow = (dayKey, idx, field, val) => {
    const existing = (s.weekly?.[dayKey] || []).slice();
    existing[idx] = { ...existing[idx], [field]: val };
    updateDay(dayKey, existing);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/booking/settings", s);
      setS(data);
      setDirty(false);
      toast.success("★ Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  const setStatus = async (bookingId, status) => {
    try {
      await api.put(`/booking/${bookingId}/status`, { status });
      setBookings((bs) => bs.map((b) => b.id === bookingId ? { ...b, status } : b));
      toast.success(`★ ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };

  const publicUrl = handle ? `${window.location.origin}/m/${handle}/book` : "";
  const copyLink = async () => {
    if (!publicUrl) return toast.error("Set your Mirror Site handle first.");
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("★ Link copied!");
    } catch (_e) {
      toast.error("Copy failed — long-press to copy by hand.");
    }
  };

  if (loading || !s) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
      <p className="italic text-sm" style={{ color: WINE }}>Loading…</p>
    </div>;
  }

  const upcoming = bookings.filter((b) => b.status !== "canceled");
  const canceled = bookings.filter((b) => b.status === "canceled");

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="appointments-page">
      {/* Header */}
      <header className="border-b-2" style={{ borderColor: `#2E9F3A55`, background: "linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 100%)" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-4 flex items-center gap-3">
          <Link to="/dashboard" data-testid="appts-back"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold border-2"
                style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
            <ArrowLeft className="w-3.5 h-3.5"/> Back To My Office
          </Link>
          <h1 className="font-serif-display text-2xl md:text-3xl uppercase ml-2" style={{ color: WINE }}>
            Appointments
          </h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-6 space-y-5">

        {/* ───── PUBLIC LINK BAR ───── */}
        <div className="rounded-2xl bg-white border-2 shadow-md p-5"
             style={{ borderColor: O }} data-testid="appts-link-bar">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: O }}>
              <Calendar className="w-5 h-5 text-white"/>
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
                ★ YOUR BOOKING PAGE
              </div>
              <h2 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: WINE }}>
                One Link. Anyone Can Book A Time.
              </h2>
              {publicUrl ? (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-mono text-sm" style={{ background: "#EAF6FC", color: WINE, border: `1.5px solid #2E9F3A55` }}>
                  {publicUrl}
                </div>
              ) : (
                <p className="text-sm mt-2 italic text-foreground/60">
                  Pick a handle on <Link to="/dashboard?tab=mirror" className="font-bold underline" style={{ color: WINE }}>Your Mirror Site</Link> first, then your booking link will show here.
                </p>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyLink} disabled={!publicUrl}
                      data-testid="appts-copy-link"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold text-white shadow disabled:opacity-50"
                      style={{ background: G }}>
                <Copy className="w-3.5 h-3.5"/> Copy Link
              </button>
              {publicUrl && (
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                   data-testid="appts-open-link"
                   className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.18em] font-bold border-2"
                   style={{ borderColor: WINE, color: WINE, background: "#FFFFFF" }}>
                  <ExternalLink className="w-3.5 h-3.5"/> Open
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ───── ON/OFF + BASIC SETTINGS ───── */}
        <div className="rounded-2xl bg-white border shadow-sm p-5" style={{ borderColor: `#2E9F3A55` }}>
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input type="checkbox" checked={!!s.enabled}
                   onChange={(e) => update("enabled", e.target.checked)}
                   data-testid="appts-enabled"
                   className="w-5 h-5"/>
            <span className="text-base font-bold" style={{ color: WINE }}>
              {s.enabled ? "★ Bookings are ON — folks can book you" : "Bookings are OFF — turn on to let folks book"}
            </span>
          </label>

          <div className="space-y-3">
            <Field label="Page title (top of your booking page)">
              <input type="text" value={s.page_title || ""} maxLength={160}
                     onChange={(e) => update("page_title", e.target.value)}
                     placeholder="Book A Time With Me"
                     data-testid="appts-title"
                     className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                     style={{ border: `2px solid #2E9F3A55`, color: WINE }}/>
            </Field>
            <Field label="Friendly note (one or two short sentences)">
              <textarea value={s.intro || ""} maxLength={600} rows={2}
                        onChange={(e) => update("intro", e.target.value)}
                        placeholder="Pick a day and time that works — I'll be there."
                        data-testid="appts-intro"
                        className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2 focus:ring-[#F39A1F]"
                        style={{ border: `2px solid #2E9F3A55`, color: WINE }}/>
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="How long is each appointment (minutes)?">
                <select value={s.duration_min || 30}
                        onChange={(e) => update("duration_min", parseInt(e.target.value, 10))}
                        data-testid="appts-duration"
                        className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2"
                        style={{ border: `2px solid #2E9F3A55`, color: WINE }}>
                  {[15, 20, 30, 45, 60, 90, 120].map((n) => (
                    <option key={n} value={n}>{n} minutes</option>
                  ))}
                </select>
              </Field>
              <Field label="How many days ahead can folks book?">
                <select value={s.days_advance || 14}
                        onChange={(e) => update("days_advance", parseInt(e.target.value, 10))}
                        data-testid="appts-advance"
                        className="w-full px-3 py-3 rounded-md text-base bg-white outline-none focus:ring-2"
                        style={{ border: `2px solid #2E9F3A55`, color: WINE }}>
                  {[7, 14, 21, 30, 45, 60].map((n) => (
                    <option key={n} value={n}>{n} days out</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        </div>

        {/* ───── WEEKLY HOURS ───── */}
        <div className="rounded-2xl bg-white border-2 shadow-md p-5" style={{ borderColor: NAVY }}>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5" style={{ color: NAVY }}/>
            <h3 className="font-serif-display text-xl uppercase" style={{ color: NAVY }}>
              When You're Open (Weekly)
            </h3>
          </div>
          <p className="text-sm text-foreground/65 mb-4">
            Add one or more time windows per day. Folks can only book inside these hours.
            Leave a day empty if you don't take appointments that day.
          </p>

          <div className="space-y-3">
            {DAYS.map((d) => {
              const windows = s.weekly?.[d.key] || [];
              return (
                <div key={d.key} className="grid sm:grid-cols-[120px_1fr_auto] items-start gap-3 p-3 rounded-lg"
                     style={{ background: "#FFFFFF", border: `1.5px solid ${O}33` }}
                     data-testid={`appts-day-${d.key}`}>
                  <div className="text-base font-bold uppercase tracking-wide pt-2" style={{ color: WINE }}>
                    {d.label}
                  </div>
                  <div className="space-y-2">
                    {windows.length === 0 && (
                      <div className="text-sm italic text-foreground/55">Closed</div>
                    )}
                    {windows.map((w, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <input type="time" value={w.start}
                               onChange={(e) => editWindow(d.key, i, "start", e.target.value)}
                               data-testid={`appts-${d.key}-start-${i}`}
                               className="px-2 py-1.5 rounded-md text-base bg-white outline-none"
                               style={{ border: `1.5px solid #2E9F3A55`, color: WINE }}/>
                        <span className="text-sm" style={{ color: WINE }}>to</span>
                        <input type="time" value={w.end}
                               onChange={(e) => editWindow(d.key, i, "end", e.target.value)}
                               data-testid={`appts-${d.key}-end-${i}`}
                               className="px-2 py-1.5 rounded-md text-base bg-white outline-none"
                               style={{ border: `1.5px solid #2E9F3A55`, color: WINE }}/>
                        <button type="button" onClick={() => removeWindow(d.key, i)}
                                data-testid={`appts-${d.key}-remove-${i}`}
                                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-bold border-2 hover:bg-red-50"
                                style={{ borderColor: "#C0392B", color: "#C0392B" }}>
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => addWindow(d.key)}
                          data-testid={`appts-${d.key}-add`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] uppercase tracking-[0.16em] font-bold text-white shadow"
                          style={{ background: "#2E9F3A" }}>
                    <Plus className="w-3.5 h-3.5"/> Add Hours
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ───── SAVE BAR ───── */}
        <div className="sticky bottom-3 z-10 rounded-2xl p-4 flex items-center gap-3 shadow-lg bg-white"
             style={{ border: `3px solid ${G}` }}>
          <button onClick={save} disabled={!dirty || saving}
                  data-testid="appts-save"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow disabled:opacity-50"
                  style={{ background: G }}>
            <Save className="w-4 h-4"/> {saving ? "Saving…" : (dirty ? "★ Save Changes" : "Saved")}
          </button>
          {dirty && <span className="text-sm italic text-foreground/65">You have unsaved changes.</span>}
        </div>

        {/* ───── BOOKINGS LIST ───── */}
        <div className="rounded-2xl bg-white border shadow-sm p-5" style={{ borderColor: `${WINE}55` }} data-testid="appts-bookings-list">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5" style={{ color: WINE }}/>
            <h3 className="font-serif-display text-xl uppercase" style={{ color: WINE }}>
              Your Bookings
            </h3>
          </div>
          {upcoming.length === 0 && canceled.length === 0 && (
            <p className="text-sm italic text-foreground/60">
              No bookings yet. Share your link — they'll show up here automatically.
            </p>
          )}
          {upcoming.length > 0 && (
            <div className="space-y-2">
              {upcoming.map((b) => (
                <BookingRow key={b.id} b={b} onConfirm={() => setStatus(b.id, "confirmed")} onCancel={() => setStatus(b.id, "canceled")}/>
              ))}
            </div>
          )}
          {canceled.length > 0 && (
            <>
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold mt-5 mb-2" style={{ color: "#9A8B76" }}>
                Canceled
              </div>
              <div className="space-y-2 opacity-60">
                {canceled.map((b) => (
                  <BookingRow key={b.id} b={b} />
                ))}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.18em] uppercase font-bold mb-1.5" style={{ color: WINE }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function BookingRow({ b, onConfirm, onCancel }) {
  const statusColor = b.status === "confirmed" ? G : b.status === "canceled" ? "#9A8B76" : O;
  return (
    <div className="rounded-lg p-3 flex flex-wrap items-start gap-3" style={{ background: "#FFFFFF", border: `1.5px solid ${O}33` }} data-testid={`booking-row-${b.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-base" style={{ color: WINE }}>{b.visitor_name}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full" style={{ background: `${statusColor}22`, color: statusColor }}>
            {b.status}
          </span>
        </div>
        <div className="text-sm mt-0.5" style={{ color: NAVY }}>
          <strong>{b.date}</strong> · {b.time} · {b.duration_min} min
        </div>
        <div className="text-xs mt-1 flex flex-wrap gap-3 text-foreground/70">
          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3"/> {b.visitor_email}</span>
          {b.visitor_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/> {b.visitor_phone}</span>}
        </div>
        {b.note && (
          <p className="text-xs italic mt-1.5 text-foreground/75">"{b.note}"</p>
        )}
      </div>
      {onConfirm && b.status !== "confirmed" && (
        <button onClick={onConfirm} data-testid={`booking-confirm-${b.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.16em] text-white"
                style={{ background: G }}>
          <CheckCircle2 className="w-3.5 h-3.5"/> Confirm
        </button>
      )}
      {onCancel && b.status !== "canceled" && (
        <button onClick={onCancel} data-testid={`booking-cancel-${b.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.16em] border-2"
                style={{ borderColor: "#C0392B", color: "#C0392B" }}>
          <X className="w-3.5 h-3.5"/> Cancel
        </button>
      )}
    </div>
  );
}
